import { Box, Center, type CenterProps, type ColorProps } from '@chakra-ui/react'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '6xl'

type IconDisplayInfo = {
  fontSize: string
  boxSideLength: string
}

const ICON_SIZE_TO_DISPLAY_INFO: Record<IconSize, IconDisplayInfo> = {
  xs: {
    fontSize: '12px',
    boxSideLength: '16px',
  },
  sm: {
    fontSize: '14px',
    boxSideLength: '18px',
  },
  md: {
    fontSize: '16px',
    boxSideLength: '20px',
  },
  lg: {
    fontSize: '20px',
    boxSideLength: '24px',
  },
  xl: {
    fontSize: '24px',
    boxSideLength: '28px',
  },
  '2xl': {
    fontSize: '32px',
    boxSideLength: '36px',
  },
  '3xl': {
    fontSize: '40px',
    boxSideLength: '44px',
  },
  '6xl': {
    fontSize: '64px',
    boxSideLength: '68px',
  },
}

export type IconProps = {
  size?: IconSize
  faIcon: IconDefinition
  color?: ColorProps['color']
  backgroundColor?: ColorProps['color']
  overrideBoxSideLength?: CenterProps['height']
  rounded?: boolean
  omitContainer?: boolean
}

export default function Icon({
  size = 'md',
  faIcon,
  color,
  backgroundColor,
  overrideBoxSideLength,
  rounded = false,
  omitContainer = false,
}: IconProps): JSX.Element {
  const { fontSize, boxSideLength } = ICON_SIZE_TO_DISPLAY_INFO[size]

  if (omitContainer) {
    return (
      <Box color={color}>
        <FontAwesomeIcon icon={faIcon} fontSize={fontSize} />
      </Box>
    )
  }

  return (
    <Center
      minWidth={overrideBoxSideLength ?? boxSideLength}
      width={overrideBoxSideLength ?? boxSideLength}
      height={overrideBoxSideLength ?? boxSideLength}
      color={color}
      justifyContent="center"
      backgroundColor={backgroundColor}
      borderRadius={rounded ? '8px' : undefined}
    >
      <FontAwesomeIcon icon={faIcon} fontSize={fontSize} />
    </Center>
  )
}
